import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "market-header-details" }
const _hoisted_2 = { class: "market-header-details__item current-price" }
const _hoisted_3 = { class: "market-header-details__label" }
const _hoisted_4 = {
  key: 1,
  class: "market-header-details__value"
}
const _hoisted_5 = { class: "market-header-details__item h24-change" }
const _hoisted_6 = { class: "market-header-details__label" }
const _hoisted_7 = {
  key: 1,
  class: "market-header-details__value"
}
const _hoisted_8 = {
  key: 0,
  class: "market-header-details__item h24-height"
}
const _hoisted_9 = { class: "market-header-details__label" }
const _hoisted_10 = {
  key: 1,
  class: "market-header-details__value"
}
const _hoisted_11 = {
  key: 1,
  class: "market-header-details__item h24-low"
}
const _hoisted_12 = { class: "market-header-details__label" }
const _hoisted_13 = {
  key: 1,
  class: "market-header-details__value"
}
const _hoisted_14 = {
  key: 2,
  class: "market-header-details__item h24-volume"
}
const _hoisted_15 = { class: "market-header-details__label-group" }
const _hoisted_16 = { class: "market-header-details__label" }
const _hoisted_17 = {
  key: 1,
  class: "market-header-details__value"
}
const _hoisted_18 = {
  key: 3,
  class: "market-header-details__item funding-rate"
}
const _hoisted_19 = /*#__PURE__*/_createElementVNode("span", { class: "market-header-details__label" }, "Funding / Countdown", -1)
const _hoisted_20 = {
  key: 0,
  class: "market-header-details__value-group"
}
const _hoisted_21 = { class: "market-header-details__countdown" }
const _hoisted_22 = {
  key: 1,
  class: "market-header-details__value"
}

import { useBreakpoints, useIntervalFn } from '@vueuse/core';
import { computed, ref, watch, type PropType } from 'vue';
import { VTooltip, VIcon } from 'vuetify/components';

import BigNumber from '@exchange/helpers/bignumber';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { useFundingRate } from '@exchange/libs/funding-rate/service/src';
import type { FundingRateData } from '@exchange/libs/funding-rate/service/src/lib/funding-rate-model.js';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';


export default /*#__PURE__*/_defineComponent({
  __name: 'MarketHeaderDetails',
  props: {
  currencies: { type: Object as PropType<Dictionary<CurrencyModel>> },
  defaultFiatCurrency: { type: String, required: true },
  market: { type: Object as PropType<MarketModel> },
},
  setup(__props) {

const props = __props;
const { isSMAndDown } = useMQBreakpoints();
const defaultPrecision = {
  percentage: CONSTANTS.PRECISION.DEFAULT_PERCENTAGE,
  zero: CONSTANTS.PRECISION.ZERO,
};

const fiatVolume = computed(() => {
  if (!props.market || !props.market.allDay.globalVolume || !props.currencies || !props.currencies[props.market.quote.code]?.defaultFiatValue) {
    return Number.NaN;
  }

  return new BigNumber(props.market.allDay.globalVolume).times(props.currencies[CONSTANTS.DEFAULT_FOR_CURRENCY_FORMATTING]?.defaultFiatValue ?? 0);
});
const priceChangeChecked = ref(false);
const priceIncreased = ref(false);
const lastCheckedMarket = ref(props.market?.name || '');

watch(
  () => props.market?.lastPrice,
  (newVal, oldVal) => {
    if (lastCheckedMarket.value === props.market?.name) {
      if (newVal && oldVal && newVal !== oldVal) {
        priceChangeChecked.value = true;
        priceIncreased.value = newVal > oldVal;
      }
    } else {
      priceChangeChecked.value = false;
      lastCheckedMarket.value = props.market?.name || '';
    }
  },
);

const fundingRateData = ref<FundingRateData | null>(null);
const { fundingRate, isLoading: isFundingRateLoading } = useFundingRate(computed(() => props.market?.id || ''));

watch(
  [fundingRate, () => props.market?.type],
  ([newVal, marketType]) => {
    if (marketType === MarketType.PERP && newVal) {
      fundingRateData.value = newVal;
    }
  },
  { immediate: true },
);

const getNextFundingTime = () => {
  const now = new Date();
  // Round down to the start of the current hour
  now.setMinutes(0, 0, 0);

  // Get the next 4-hour interval
  const currentHour = now.getUTCHours();
  const nextInterval = Math.ceil((currentHour + 1) / 4) * 4;

  const next = new Date(now);
  next.setUTCHours(nextInterval, 0, 0, 0);

  // If we're already past the calculated time, add 4 hours
  if (next <= now) {
    next.setTime(next.getTime() + 4 * 60 * 60 * 1000);
  }

  return next;
};

const nextFundingTime = ref(getNextFundingTime());

const countdown = ref('');

const updateCountdown = () => {
  const now = new Date();
  const diff = nextFundingTime.value.getTime() - now.getTime();

  if (diff <= 0) {
    // Reset the next funding time
    nextFundingTime.value = getNextFundingTime();
    updateCountdown();
    return;
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  countdown.value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Update countdown every second
useIntervalFn(updateCountdown, 1000);

const isFutures = computed(() => props.market?.type === MarketType.PERP);

const getTooltip = (d: { message: string; html?: boolean }) => ({
  content: d.message,
  placement: 'bottom',
  html: d.html,
});

return (_ctx: any,_cache: any) => {
  const _component_ot_number_display = _resolveComponent("ot-number-display")!
  const _component_ot_icon = _resolveComponent("ot-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('modules.marketDetails.currentPrice')), 1),
      (__props.market)
        ? (_openBlock(), _createBlock(_component_ot_number_display, {
            key: 0,
            class: _normalizeClass(["market-header-details__value", { [`market-header-details__last-price-${priceIncreased.value ? 'arrow-up' : 'arrow-down'}`]: priceChangeChecked.value }]),
            grouping: true,
            "left-aligned": true,
            number: __props.market.lastPrice,
            precision: __props.market.marketPrecision,
            "smaller-suffix": true,
            suffix: __props.market.quote.code
          }, null, 8, ["class", "number", "precision", "suffix"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t('fundamentals.emptyValues.default')), 1))
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t('modules.marketDetails.24hPrice')), 1),
      (__props.market)
        ? (_openBlock(), _createBlock(_component_ot_number_display, {
            key: 0,
            class: "market-header-details__value",
            "add-plus-sign": true,
            "colored-percentage": true,
            "left-aligned": true,
            number: __props.market.priceChangePercentageToDisplay,
            percentage: true,
            precision: defaultPrecision.percentage
          }, null, 8, ["number", "precision"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.$t('fundamentals.emptyValues.default')), 1))
    ]),
    (!isFutures.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.$t('modules.marketDetails.24hHigh')), 1),
          (__props.market)
            ? (_openBlock(), _createBlock(_component_ot_number_display, {
                key: 0,
                class: "market-header-details__value",
                "suffix-class": "market-header-details__value-suffix",
                grouping: true,
                "left-aligned": true,
                number: __props.market.allDay.high,
                precision: __props.market.marketPrecision,
                "smaller-suffix": true,
                suffix: __props.market.quote.code
              }, null, 8, ["number", "precision", "suffix"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_ctx.$t('fundamentals.emptyValues.default')), 1))
        ]))
      : _createCommentVNode("", true),
    (!isFutures.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _createElementVNode("span", _hoisted_12, _toDisplayString(_ctx.$t('modules.marketDetails.24hLow')), 1),
          (__props.market)
            ? (_openBlock(), _createBlock(_component_ot_number_display, {
                key: 0,
                class: "market-header-details__value",
                "suffix-class": "market-header-details__value-suffix",
                grouping: true,
                "left-aligned": true,
                number: __props.market.allDay.low,
                precision: __props.market.marketPrecision,
                "smaller-suffix": true,
                suffix: __props.market.quote.code
              }, null, 8, ["number", "precision", "suffix"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(_ctx.$t('fundamentals.emptyValues.default')), 1))
        ]))
      : _createCommentVNode("", true),
    (!_unref(isSMAndDown))
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("span", _hoisted_16, [
              _createTextVNode(_toDisplayString(_ctx.$t('modules.marketDetails.24hVolume')) + " ", 1),
              _createVNode(_unref(VTooltip), {
                text: _ctx.$t('modules.marketDetails.globalVolumeTooltip'),
                location: "bottom"
              }, {
                activator: _withCtx(({ props }) => [
                  _createVNode(_component_ot_icon, _mergeProps(props, {
                    class: "market-header-details__info-icon",
                    name: "info-circle"
                  }), null, 16)
                ]),
                _: 1
              }, 8, ["text"])
            ])
          ]),
          (__props.market)
            ? (_openBlock(), _createBlock(_component_ot_number_display, {
                key: 0,
                class: "market-header-details__value",
                "suffix-class": "market-header-details__value-suffix",
                grouping: true,
                "left-aligned": true,
                number: fiatVolume.value,
                precision: defaultPrecision.zero,
                "smaller-suffix": true,
                suffix: __props.defaultFiatCurrency
              }, null, 8, ["number", "precision", "suffix"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_17, _toDisplayString(_ctx.$t('fundamentals.emptyValues.default')), 1))
        ]))
      : _createCommentVNode("", true),
    (isFutures.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
          _hoisted_19,
          (!_unref(isFundingRateLoading) && fundingRateData.value && countdown.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                _createVNode(_component_ot_number_display, {
                  class: "market-header-details__value",
                  "suffix-class": "market-header-details__value-suffix",
                  "add-plus-sign": false,
                  "colored-percentage": true,
                  "left-aligned": true,
                  number: Number(fundingRateData.value.funding_rate) * 100,
                  percentage: true,
                  precision: 4
                }, null, 8, ["number"]),
                _createTextVNode(" / "),
                _createElementVNode("span", _hoisted_21, _toDisplayString(countdown.value), 1)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_22, _toDisplayString(_ctx.$t('fundamentals.emptyValues.default')), 1))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})